import type { ComparisonPage } from '../types';

export const windsorVsWarehouse: ComparisonPage = {
  slug: 'windsor-ai-vs-custom-data-warehouse',
  group: 'ecomm',
  ours: 'greyfield',
  theirs: 'windsor',
  title: 'Windsor.ai vs a custom data warehouse: cheap connectors or a finished model?',
  metaTitle: 'Windsor.ai vs a Custom Ecommerce Data Warehouse (2026): Honest Comparison',
  description:
    'Windsor.ai moves data from 350+ sources into sheets, Looker Studio, or Snowflake from $19 a month. It does not model profit. Here is how it fits with a build.',
  keywords: ['windsor.ai alternative', 'windsor.ai vs', 'windsor.ai pricing', 'windsor.ai snowflake', 'marketing data connector'],
  published: '2026-11-03',
  updated: '2026-11-03',
  tldr:
    'Windsor.ai is one of the cheapest ways to get marketing and store data into Looker Studio, a spreadsheet, or your own Snowflake, and every destination is on every plan. It is a pipe, not a model: it does not calculate profit, match orders across channels, or handle refunds for you. For many brands it can be the pipeline under a custom build.',
  differences: [
    { heading: 'Connectors vs modeling', body: 'Windsor connects 350+ sources and delivers them as tables. Matching orders, dating refunds, and calculating margin happen after delivery.' },
    { heading: 'Snowflake on every plan', body: 'Unlike several competitors, Windsor includes warehouse destinations on its cheapest plans, so it can feed a build at low cost.' },
    { heading: 'Priced by sources and accounts', body: 'Plans run from $19 to $499 a month billed annually, driven by sources, accounts, and refresh frequency.' },
  ],
  sections: [
    {
      heading: 'What Windsor does well',
      paragraphs: [
        'Its connector list covers Shopify, Amazon Seller Central with fee fields, TikTok Shop, NetSuite, Recharge, and the main ad platforms. It has a free plan after a 30-day trial, unlimited users on paid plans, and a fast-moving AI story: an MCP server, connectors for Claude, ChatGPT, and Cursor, and, since May 2026, the ability to make changes in Facebook and Google Ads campaigns.',
      ],
    },
    {
      heading: 'What still needs modeling',
      paragraphs: [
        'We found no built-in profit model, pixel, or MMM. Custom fields support formulas, but the rules that make ecommerce numbers agree still have to be written: [counting each order once](/blog/avoid-double-counting-shopify-amazon-tiktok-orders) across channels, [dating refunds](/blog/shopify-refund-date-vs-order-date), and [replacing estimated Amazon fees with actual ones](/blog/why-amazon-profit-changes-after-sale).',
      ],
    },
  ],
  pickThem: [
    'You want marketing data in Looker Studio or Sheets for tens of dollars a month.',
    'You already run a warehouse and need cheap connectors into it.',
    'You want AI tools to read and act on your ad data.',
  ],
  pickUs: [
    'You need profit, refunds, and fees modeled across channels.',
    'You want tested business rules your team can read and change.',
    'You want reports built for finance and operations, not only marketing.',
  ],
  byTeam: [
    { team: 'Small marketing team on Looker Studio', pick: 'theirs', why: 'Low cost and no engineering needed.' },
    { team: 'Brand with a data engineer', pick: 'theirs', why: 'Cheap connectors into Snowflake; your engineer models.' },
    { team: 'Brand that wants margin by channel', pick: 'both', why: 'Windsor loads Snowflake; the build models profit on top.' },
  ],
  faq: [
    {
      question: 'Does a custom warehouse replace Windsor.ai?',
      answer: 'Not necessarily. Windsor can be the connector layer that loads the warehouse. A build replaces the spreadsheets and dashboards that try to model profit on raw connector tables.',
    },
    {
      question: 'Can Windsor.ai send data to Snowflake?',
      answer: 'Yes. Windsor lists Snowflake, BigQuery, Redshift, Databricks, and others as destinations on every plan.',
    },
  ],
  related: ['funnel-vs-custom-data-warehouse', 'supermetrics-vs-custom-data-warehouse', 'saras-analytics-vs-custom-data-warehouse'],
};
